import React, { useEffect, useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import clinicService from '../../../../../services/clinic.service';
import Loading from '../../../../common/Loading';
import { useParams } from 'react-router-dom';
import {
    calculateCompletionStats,
    getGroupedServices,
    getServiceIconClass,
    getServiceIconTone,
    getStatusBadgeClass,
    getStatusIconClass,
    getStatusLabel,
} from './accountStatusUtils';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

const ServiceStatusRow = ({ serviceName, status }) => (
    <div className="clinic-account-status-row">
        <span className="clinic-account-status-row__label">
            <span className={`clinic-account-status-row__icon clinic-account-status-row__icon--${getServiceIconTone(serviceName)}`}>
                <i className={`bi ${getServiceIconClass(serviceName)}`}></i>
            </span>
            {serviceName}
        </span>
        <span className={`clinic-account-status-badge ${getStatusBadgeClass(status)}`}>
            <i className={`bi ${getStatusIconClass(status)}`}></i>
            {getStatusLabel(status)}
        </span>
    </div>
);

function AccountStatus() {
    const { clinicId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [serviceStatus, setServiceStatus] = useState([]);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        loadAccountStatus();
    }, [clinicId]);

    const loadAccountStatus = () => {
        setIsLoading(true);
        setIsError(false);
        clinicService
            .getClinicAccountStatus(clinicId)
            .then((response) => {
                setServiceStatus(response.data.services || []);
            })
            .catch((err) => {
                setIsError(true);
                setErrorMessage(err.response?.data?.message || 'Failed to load account status.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(clinicId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const stats = calculateCompletionStats(serviceStatus);
    const groupedServices = getGroupedServices(serviceStatus);

    if (isLoading) {
        return <Loading />;
    }

    return (
        <div className="row g-3">
            <div className="col-lg-8 clinic-account-status-panel">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                    <div>
                        <h5 className="clinic-account-status__title mb-1">
                            <i className="bi bi-clipboard2-pulse text-primary me-1"></i>
                            Account Status
                        </h5>
                        <p className="text-muted small mb-0">Track onboarding, configuration, and setup progress for this clinic.</p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-light text-dark border font-monospace small">ID: {clinicId}</span>
                        <ActionTooltipButton
                            tooltip={copied ? 'Copied!' : 'Copy clinic ID'}
                            tooltipId="copy-clinic-account-status-id"
                            variant="outline-secondary"
                            size="sm"
                            onClick={copyToClipboard}
                        >
                            <i className={`bi ${copied ? 'bi-check2 text-success' : 'bi-copy'}`}></i>
                        </ActionTooltipButton>
                    </div>
                </div>

                <div className="notifications-panel">
                    <div className="notifications-panel__header">
                        <span className="d-flex align-items-center gap-2">
                            <i className="bi bi-list-check"></i>
                            Setup Checklist
                        </span>
                    </div>

                    <div className="notifications-panel__body">
                        {isError && (
                            <div className="alert alert-warning border-0 py-2 px-3 small mb-3">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                {errorMessage}
                            </div>
                        )}

                        <div className="clinic-account-status-toolbar">
                            <div className="d-flex flex-wrap gap-2">
                                <span className="clinic-account-status-stat clinic-account-status-stat--total">{stats.total} services</span>
                                <span className="clinic-account-status-stat clinic-account-status-stat--completed">{stats.completed} completed</span>
                                <span className="clinic-account-status-stat clinic-account-status-stat--pending">{stats.pending} pending</span>
                                <span className="clinic-account-status-stat clinic-account-status-stat--incomplete">{stats.incomplete} incomplete</span>
                            </div>
                        </div>

                        {groupedServices.map((group, index) => (
                            <div key={group.title}>
                                <div className={`clinic-account-status-section-title${index === 0 ? ' clinic-account-status-section-title--first' : ''}`}>
                                    {group.title}
                                </div>
                                {group.items.map((item) => (
                                    <ServiceStatusRow key={item.serviceName} serviceName={item.serviceName} status={item.status} />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="col-lg-4 clinic-account-status-summary-panel">
                <div className="mb-3">
                    <h5 className="clinic-account-status__title mb-1">
                        <i className="bi bi-graph-up text-primary me-1"></i>
                        Completion Overview
                    </h5>
                    <p className="text-muted small mb-0">Overall setup progress and status breakdown.</p>
                </div>

                <div className="notifications-panel">
                    <div className="notifications-panel__header">
                        <span className="d-flex align-items-center gap-2">
                            <i className="bi bi-speedometer2"></i>
                            Progress
                        </span>
                    </div>
                    <div className="notifications-panel__body">
                        <div className="clinic-account-status-progress">
                            <div className="clinic-account-status-progress__header">
                                <span className="clinic-account-status-progress__label">Completion Progress</span>
                                <span className="clinic-account-status-progress__value">
                                    {stats.percentage}% ({stats.completed}/{stats.total})
                                </span>
                            </div>
                            <div className="clinic-account-status-progress__bar">
                                <div
                                    className="clinic-account-status-progress__fill"
                                    style={{ width: `${stats.percentage}%` }}
                                    role="progressbar"
                                    aria-valuenow={stats.percentage}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                            </div>
                        </div>

                        <div className="clinic-account-status-summary-stats">
                            <div className="clinic-account-status-summary-stat">
                                <div className="clinic-account-status-summary-stat__value">{stats.percentage}%</div>
                                <div className="clinic-account-status-summary-stat__label">Overall Complete</div>
                            </div>
                            <div className="clinic-account-status-summary-stat">
                                <div className="clinic-account-status-summary-stat__value">{stats.completed}</div>
                                <div className="clinic-account-status-summary-stat__label">Completed</div>
                            </div>
                            <div className="clinic-account-status-summary-stat">
                                <div className="clinic-account-status-summary-stat__value">{stats.pending}</div>
                                <div className="clinic-account-status-summary-stat__label">Pending</div>
                            </div>
                            <div className="clinic-account-status-summary-stat">
                                <div className="clinic-account-status-summary-stat__value">{stats.incomplete}</div>
                                <div className="clinic-account-status-summary-stat__label">Incomplete</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AccountStatus;
